import React, { useState, useEffect, useRef } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useCurrentAccount, useSignPersonalMessage, ConnectButton } from '@mysten/dapp-kit';
import { useJWTAuth } from '../auth/JWTAuthContext';
import { useAccountLinking } from '../auth/useAccountLinking';
import UsernamePrompt from './UsernamePrompt';

const WalletConnectionModal = ({ isOpen, onClose, pendingUsername = null }) => {
  const { connected, publicKey, disconnect } = useWallet();
  const { login: jwtLogin, isAuthenticated } = useJWTAuth();
  const { logout: logoutAccountLinking } = useAccountLinking();
  const suiAccount = useCurrentAccount();
  const { mutateAsync: suiSignPersonalMessage } = useSignPersonalMessage();
  const [showUsernamePrompt, setShowUsernamePrompt] = useState(false);
  const [pendingWalletAddress, setPendingWalletAddress] = useState(null);
  const processedWalletRef = useRef(null);

  // Check if user needs username when wallet connects - only run once per wallet
  useEffect(() => {
    if (!connected || !publicKey) {
      processedWalletRef.current = null;
      return;
    }

    const walletAddress = publicKey.toString();
    
    // Don't process the same wallet multiple times
    if (processedWalletRef.current === walletAddress) {
      console.log('⏳ [WalletModal] Already processed this wallet, skipping...');
      return;
    }

    // Mark this wallet as processed immediately
    processedWalletRef.current = walletAddress;
    
    const checkUsernameRequirement = async () => {
      console.log('🔍 [WalletModal] Wallet connected, checking if user exists for:', walletAddress);
      
      try {
        // If we have a pending username from the landing page, use it directly
        if (pendingUsername) {
          console.log('👤 [WalletModal] Using pending username:', pendingUsername);
          const result = await jwtLogin(walletAddress, pendingUsername);
          console.log('🔍 [WalletModal] JWT login with username result:', result);
          
          if (result.success) {
            console.log('✅ [WalletModal] Login with username successful, closing modal');
            onClose();
          } else {
            console.log('❌ [WalletModal] Login with username failed:', result.error);
          }
        } else {
          // No pending username, try to login existing user first
          console.log('🔍 [WalletModal] Attempting auto-login for existing user...');
          const result = await jwtLogin(walletAddress);
          console.log('🔍 [WalletModal] JWT login result:', result);
          
          if (result.success && result.needsUsername) {
            console.log('👤 [WalletModal] New user detected, username required');
            setPendingWalletAddress(walletAddress);
            setShowUsernamePrompt(true);
          } else if (result.success) {
            console.log('✅ [WalletModal] Existing user auto-login successful, closing modal');
            onClose();
          } else {
            console.log('❌ [WalletModal] Login failed:', result.error);
            // Show error or fallback to username prompt
            setPendingWalletAddress(walletAddress);
            setShowUsernamePrompt(true);
          }
        }
      } catch (error) {
        console.error('❌ [WalletModal] Error checking username requirement:', error);
        // On error, show username prompt as fallback
        setPendingWalletAddress(walletAddress);
        setShowUsernamePrompt(true);
      }
    };

    checkUsernameRequirement();
  }, [connected, publicKey, pendingUsername]); // Include pendingUsername in dependencies

  // Handle SUI wallet connection
  useEffect(() => {
    if (!suiAccount) return;

    const walletAddress = suiAccount.address;
    console.log('🔍 [WalletModal] SUI wallet connected, checking username requirement for:', walletAddress);
    
    // Don't process the same wallet multiple times
    if (processedWalletRef.current === `sui-${walletAddress}`) {
      console.log('⏳ [WalletModal] Already processed this SUI wallet, skipping...');
      return;
    }

    // Mark this wallet as processed immediately
    processedWalletRef.current = `sui-${walletAddress}`;
    
    const checkSUIUsernameRequirement = async () => {
      try {
        // If we have a pending username from the landing page, use it directly
        if (pendingUsername) {
          console.log('👤 [WalletModal] Using pending username for SUI:', pendingUsername);
          const result = await jwtLogin(walletAddress, pendingUsername);
          console.log('🔍 [WalletModal] JWT login with username result for SUI:', result);
          
          if (result.success) {
            console.log('✅ [WalletModal] SUI login with username successful, closing modal');
            onClose();
          } else {
            console.log('❌ [WalletModal] SUI login with username failed:', result.error);
          }
        } else {
          // No pending username, try to login existing user first
          console.log('🔍 [WalletModal] Attempting auto-login for existing SUI user...');
          const result = await jwtLogin(walletAddress);
          console.log('🔍 [WalletModal] JWT login result for SUI:', result);
          
          if (result.success && result.needsUsername) {
            console.log('👤 [WalletModal] New SUI user detected, username required');
            setPendingWalletAddress(walletAddress);
            setShowUsernamePrompt(true);
          } else if (result.success) {
            console.log('✅ [WalletModal] Existing SUI user auto-login successful, closing modal');
            onClose();
          } else {
            console.log('❌ [WalletModal] SUI login failed:', result.error);
            // Show error or fallback to username prompt
            setPendingWalletAddress(walletAddress);
            setShowUsernamePrompt(true);
          }
        }
      } catch (error) {
        console.error('❌ [WalletModal] Error with SUI wallet connection:', error);
        setPendingWalletAddress(walletAddress);
        setShowUsernamePrompt(true);
      }
    };

    checkSUIUsernameRequirement();
  }, [suiAccount, pendingUsername]);

  const handleUsernameSubmit = async (username) => {
    console.log('👤 [WalletModal] Username submit called:', {
      username,
      pendingWalletAddress
    });

    if (!pendingWalletAddress) {
      throw new Error('No pending wallet address');
    }

    try {
      console.log('🔍 [WalletModal] Calling JWT login with username...');
      const result = await jwtLogin(pendingWalletAddress, username);
      console.log('🔍 [WalletModal] Username submit result:', result);
      
      if (result.success) {
        console.log('✅ [WalletModal] Username set successfully, closing modals');
        setShowUsernamePrompt(false);
        setPendingWalletAddress(null);
        onClose();
      } else {
        throw new Error(result.error || 'Failed to set username');
      }
    } catch (error) {
      console.error('❌ [WalletModal] Error setting username:', error);
      throw error;
    }
  };

  const handleUsernamePromptClose = () => {
    setShowUsernamePrompt(false);
    setPendingWalletAddress(null);
  };

  const handleUsernamePromptCancel = async () => {
    console.log('🚫 [WalletModal] Username prompt cancelled, disconnecting wallet');
    try {
      // Disconnect wallet and clear all auth data
      await disconnect();
      logoutAccountLinking();
      setShowUsernamePrompt(false);
      setPendingWalletAddress(null);
      processedWalletRef.current = null; // Reset processed wallet ref
      // Don't close the modal - let user try connecting again
    } catch (error) {
      console.error('❌ [WalletModal] Error disconnecting wallet:', error);
    }
  };

  if (!isOpen) return null;


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4 border border-gray-700">
        <div className="text-center mb-6">
          <div className="text-4xl mb-4">🔗</div>
          {pendingUsername ? (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">Hi, {pendingUsername}!</h2>
              <p className="text-gray-300">
                How will you connect?
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">Connect Your Wallet</h2>
              <p className="text-gray-300">
                Choose a wallet to connect and start your SoulWars journey.
              </p>
            </>
          )}
        </div>

        <div className="space-y-4">
          {/* Solana/Phantom Wallet Button */}
          <div className="w-full">
            <WalletMultiButton
              className="!w-full !bg-gradient-to-r !from-purple-600 !to-purple-700 hover:!from-purple-700 hover:!to-purple-800 !text-white !font-bold !py-4 !px-6 !rounded-lg !text-lg !transition-all !duration-200 !transform hover:!scale-105 !shadow-lg hover:!shadow-xl !border-none"
              style={{
                background: 'linear-gradient(to right, #9333ea, #7c3aed)',
                borderRadius: '0.5rem',
                padding: '1rem 1.5rem',
                fontSize: '1.125rem',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                width: '100%'
              }}
            >
              🟣 Connect SOL Wallet
            </WalletMultiButton>
          </div>
          
          {/* SUI Wallet Button */}
          <div className="w-full">
            <ConnectButton
              connectText="🔷 Connect SUI Wallet"
              className="!w-full !bg-gradient-to-r !from-blue-600 !to-cyan-600 hover:!from-blue-700 hover:!to-cyan-700 !text-white !font-bold !py-3 !px-4 !rounded-lg !text-base !transition-all !duration-200 !transform hover:!scale-105 !shadow-lg hover:!shadow-xl !border-none"
              style={{
                background: 'linear-gradient(to right, #2563eb, #0891b2)',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                fontSize: '1rem',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                width: '100%'
              }}
            />
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-700">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </div>

        <div className="mt-4 text-xs text-gray-400 text-center">
          <p>Don't have a wallet? <a href="https://phantom.app/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Get Phantom</a> or <a href="https://sui.io/wallet" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Get SUI Wallet</a></p>
        </div>
      </div>
      
      {/* Username Prompt Modal */}
      <UsernamePrompt
        isOpen={showUsernamePrompt}
        onClose={handleUsernamePromptClose}
        onSubmit={handleUsernameSubmit}
        onCancel={handleUsernamePromptCancel}
        walletAddress={pendingWalletAddress}
      />
    </div>
  );
};

export default WalletConnectionModal;
